import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useCurrency } from "../context/CurrencyContext";
import { useWishlist } from "../context/WishlistContext";
import { useLang } from "../context/LanguageContext";
import { CODE_TO_URL } from "../lib/i18nRouter";
import { toast } from "sonner";
import VideoBadge from "./VideoBadge";
import { cdnTransform } from "../lib/cloudinary";

export const GemstoneCard = ({ gem }) => {
  const { format } = useCurrency();
  const wl = useWishlist();
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  const inList = wl?.has(gem.id);

  const onHeart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const added = await wl.toggle(gem.id);
    toast.success(added ? "Saved to wishlist" : "Removed from wishlist");
  };

  return (
    <Link
      to={`/${slug}/gemstone/${gem.id}`}
      data-testid={`gem-card-${gem.id}`}
      className="group block bg-onyx border border-white/5 hover:border-gold/40 transition-all duration-700 relative"
    >
      <button
        data-testid={`wishlist-${gem.id}`}
        onClick={onHeart}
        aria-label={inList ? "Remove from wishlist" : "Add to wishlist"}
        className={`absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center bg-black/60 backdrop-blur-sm border transition-all duration-500 ${
          inList ? "border-gold text-gold" : "border-white/20 text-white/70 hover:border-gold hover:text-gold"
        }`}
      >
        <Heart size={16} fill={inList ? "currentColor" : "none"} />
      </button>
      <div className="relative overflow-hidden aspect-[4/5] bg-black">
        <img
          src={cdnTransform(gem.image_url, "w_800,q_auto,f_auto")}
          alt={gem.images?.[0]?.alt || gem.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[1200ms] ease-out"
        />
        <div className="absolute top-4 left-4 font-accent text-[10px] tracking-[0.28em] uppercase text-gold/90 bg-black/60 px-3 py-1.5">
          {gem.category}
        </div>
        {gem.video?.url && (
          <VideoBadge className="absolute bottom-4 right-4" />
        )}
        {gem.vip_only && (
          <div className="absolute bottom-4 left-4 font-accent text-[10px] tracking-[0.28em] uppercase text-black bg-gold px-3 py-1.5">
            VIP
          </div>
        )}
      </div>
      <div className="px-5 py-6 border-t border-white/5">
        <div className="flex items-baseline justify-between gap-3 mb-3">
          <h3 className="font-heading text-2xl font-light leading-tight">{gem.name}</h3>
        </div>
        <div className="flex items-center justify-between text-[11px] tracking-[0.18em] uppercase text-white/50 font-accent">
          <span>{gem.carat || gem.weight_value || ""} {gem.weight_unit || "ct"} · {gem.origin}</span>
          <span className="text-gold">{gem.certification}</span>
        </div>
        <div className="mt-5">
          <span className="font-heading text-lg text-ivory">{format(gem.reserve_price, gem.currency)}</span>
        </div>
      </div>
    </Link>
  );
};

export default GemstoneCard;
